import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, X, Send, Sparkles, MapPin, ArrowRight, Trash2 } from "lucide-react";

const SUGGESTED_PROMPTS = [
  "Je cherche un fleuriste",
  "Où boire un café en terrasse ?",
  "Quelles sont les actualités des commerçants ?",
  "Comment gagner un bon de réduction ?",
];

/**
 * Assistant IA flottant — "Élise" la concierge virtuelle d'Istres & Moi.
 * Bulle en bas à droite, ouvre un panneau de chat avec suggestions cliquables.
 * Pour les utilisateurs connectés, l'historique et les préférences sont conservés.
 */
export default function AssistantBubble() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [preferences, setPreferences] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [messages, setMessages] = useState(() => [defaultGreeting(null)]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  // Charge l'historique au montage et quand l'utilisateur se connecte/déconnecte
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setMessages([defaultGreeting(null)]);
      setPreferences([]);
      setFirstName("");
      setHydrated(true);
      return;
    }
    api.get("/assistant/history")
      .then(({ data }) => {
        if (cancelled) return;
        const past = (data.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
          suggestions: [],
        }));
        setPreferences(data.preferences || []);
        setFirstName(data.first_name || "");
        if (past.length > 0) {
          // Greeting personnalisé en tête + historique
          setMessages([defaultGreeting(data.first_name, true), ...past]);
        } else {
          setMessages([defaultGreeting(data.first_name)]);
        }
      })
      .catch(() => setMessages([defaultGreeting(user?.name?.split(" ")[0] || "")]))
      .finally(() => !cancelled && setHydrated(true));
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  // Écoute l'événement global pour ouvrir l'assistant avec un message pré-rempli
  // (ex: bouton "Élise" sur un post du Mur des commerçants)
  useEffect(() => {
    const handler = (e) => {
      const { prefill, autoSend } = e.detail || {};
      setOpen(true);
      if (prefill) {
        if (autoSend) {
          // Petite tempo pour laisser le panneau s'ouvrir
          setTimeout(() => send(prefill), 200);
        } else {
          setInput(prefill);
        }
      }
    };
    window.addEventListener("istres-assistant-open", handler);
    return () => window.removeEventListener("istres-assistant-open", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { question: q });
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: data.answer,
          suggestions: data.suggestions || [],
          redirect_url: data.redirect_url,
          redirect_label: data.redirect_label,
          category: data.category,
          merchants: data.merchants_sample || [],
        },
      ]);
    } catch (_) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Désolée, je n'arrive pas à répondre pour le moment. Réessayez dans quelques instants." },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const clearHistory = async () => {
    if (!window.confirm("Effacer toute votre conversation et les préférences mémorisées par Élise ?")) return;
    try {
      await api.delete("/assistant/history");
      setPreferences([]);
      setMessages([defaultGreeting(firstName)]);
    } catch (_) {
      // best-effort
    }
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assistant-bubble-open"
          aria-label="Ouvrir l'assistant"
          className="fixed bottom-5 right-5 sm:bottom-7 sm:right-7 z-40 group"
        >
          <span className="relative flex">
            <span className="absolute inset-0 rounded-full bg-pink-500 animate-ping opacity-30" />
            <span className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-pink-500 to-plum-700 text-white grid place-items-center shadow-xl shadow-pink-500/40 group-hover:scale-105 transition-transform">
              <Sparkles size={22} strokeWidth={2.2} />
            </span>
          </span>
          <span className="absolute -top-1 -right-1 bg-sun-400 text-plum-900 text-[9px] font-extrabold px-1.5 py-0.5 rounded-full uppercase tracking-wider">IA</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed inset-0 z-50 sm:inset-auto sm:bottom-7 sm:right-7 sm:w-[400px] sm:max-h-[640px] flex flex-col bg-white sm:rounded-3xl shadow-2xl shadow-plum-900/30 overflow-hidden animate-in slide-in-from-bottom-8 fade-in duration-300" data-testid="assistant-panel">
          {/* Header */}
          <header className="flex items-center gap-3 px-4 py-3 bg-gradient-to-br from-plum-800 via-plum-700 to-pink-600 text-white shrink-0">
            <div className="w-10 h-10 rounded-2xl bg-white/15 grid place-items-center backdrop-blur-sm">
              <Sparkles size={18} strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-extrabold text-[15px] leading-tight">
                Élise · Concierge IA {user && firstName && <span className="text-sun-300 font-medium ml-1">· {firstName}</span>}
              </div>
              <div className="text-[11px] text-white/75 flex items-center gap-1.5">
                <span className="relative inline-flex w-2 h-2">
                  <span className="absolute inset-0 rounded-full bg-mint-300 animate-ping opacity-75" />
                  <span className="relative w-2 h-2 rounded-full bg-mint-400" />
                </span>
                {user ? `${preferences.length > 0 ? `${preferences.length} préférence${preferences.length > 1 ? 's' : ''} mémorisée${preferences.length > 1 ? 's' : ''}` : "Conversation conservée"}` : "En ligne · répond en quelques secondes"}
              </div>
            </div>
            {user && messages.length > 1 && (
              <button
                onClick={clearHistory}
                data-testid="assistant-clear-history"
                aria-label="Effacer l'historique"
                title="Effacer ma conversation et mes préférences"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-pink-500/40 grid place-items-center transition-colors"
              >
                <Trash2 size={14} />
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              data-testid="assistant-bubble-close"
              aria-label="Fermer"
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center transition-colors"
            >
              <X size={16} />
            </button>
          </header>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3.5 bg-cream-50" data-testid="assistant-messages">
            {messages.map((m, i) => (
              <MessageBubble key={i} msg={m} onClose={() => setOpen(false)} />
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-plum-700/55 text-[12px]">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
                <span>Élise réfléchit…</span>
              </div>
            )}
          </div>

          {/* Suggested prompts (only on first turn AND no past history) */}
          {messages.length === 1 && hydrated && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5 shrink-0 bg-cream-50" data-testid="assistant-suggested-prompts">
              {SUGGESTED_PROMPTS.map((p, i) => (
                <button
                  key={i}
                  onClick={() => send(p)}
                  data-testid={`assistant-prompt-${i}`}
                  className="text-[11px] font-semibold text-plum-700 bg-white border border-cream-200 hover:border-pink-300 hover:bg-pink-50 rounded-full px-3 py-1.5 transition-colors"
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-cream-200 bg-white shrink-0">
            <div className="flex items-center gap-2 rounded-2xl bg-cream-50 border border-cream-200 focus-within:border-pink-400 transition-colors px-3 py-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Posez votre question…"
                data-testid="assistant-input"
                className="flex-1 bg-transparent text-[14px] outline-none text-plum-900 placeholder-plum-700/45"
              />
              <button
                onClick={() => send()}
                disabled={!input.trim() || busy}
                data-testid="assistant-send"
                aria-label="Envoyer"
                className="w-9 h-9 rounded-xl bg-gradient-to-br from-pink-500 to-plum-700 text-white grid place-items-center shadow-md disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg transition-shadow"
              >
                <Send size={14} strokeWidth={2.4} />
              </button>
            </div>
            <p className="text-[10px] text-plum-700/45 mt-1.5 italic text-center">
              Assistant IA — peut faire des erreurs, vérifiez les infos importantes.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function MessageBubble({ msg, onClose }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] ${isUser ? "" : "w-full"}`}>
        <div
          className={`px-3.5 py-2.5 rounded-2xl text-[13.5px] leading-relaxed ${
            isUser
              ? "bg-plum-700 text-white rounded-br-sm shadow-sm"
              : "bg-white text-plum-900 rounded-bl-sm border border-cream-200 shadow-sm"
          }`}
          data-testid={isUser ? "assistant-msg-user" : "assistant-msg-bot"}
        >
          {msg.content}
        </div>

        {/* Merchants sample (clickable cards) */}
        {msg.merchants && msg.merchants.length > 0 && (
          <div className="mt-2 space-y-1.5" data-testid="assistant-merchants">
            {msg.merchants.slice(0, 3).map((m, i) => (
              <div key={i} className="flex items-start gap-2 p-2.5 rounded-xl bg-white border border-cream-200">
                <div className="w-8 h-8 rounded-lg bg-sun-100 text-sun-700 grid place-items-center text-base shrink-0">🏬</div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-[12px] text-plum-900 truncate">{m.name}</div>
                  <div className="text-[10px] text-plum-700/60 line-clamp-1 flex items-center gap-1">
                    <MapPin size={9} strokeWidth={2.3} /> {m.address}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Suggestions / redirect CTA */}
        {msg.suggestions && msg.suggestions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="assistant-suggestions">
            {msg.suggestions.map((s, i) => (
              <Link
                key={i}
                to={s.url}
                onClick={onClose}
                data-testid={`assistant-suggestion-${i}`}
                className="inline-flex items-center gap-1.5 text-[11px] font-bold text-plum-700 bg-sun-50 border border-sun-200 hover:bg-sun-100 rounded-full px-3 py-1.5 transition-colors"
              >
                {s.label} <ArrowRight size={10} />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
 * Helpers
 * ============================================================ */
function defaultGreeting(firstName, returning = false) {
  if (firstName) {
    return {
      role: "assistant",
      content: returning
        ? `Re-bonjour ${firstName} ! Heureuse de vous revoir. Je me souviens de nos précédents échanges — comment puis-je vous aider aujourd'hui ?`
        : `Bonjour ${firstName} ! Je suis Élise, la concierge d'Istres & Moi. Vous cherchez un commerce, un événement ou un bon ? Je me souviendrai aussi de vos préférences pour mieux vous orienter la prochaine fois.`,
      suggestions: [],
    };
  }
  return {
    role: "assistant",
    content: "Bonjour, je suis Élise, la concierge d'Istres & Moi. Vous cherchez un commerce, un événement, un bon ? Posez votre question !",
    suggestions: [],
  };
}

